import { Stack, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useCallback, useMemo } from "react";
import { filterStatType } from "../../../../modules/stats/server/analytics-service";
import { EmptyState } from "../generic/empty-state";
import { ExportToolbar } from "../generic/export-toolbar";
import { calculatePercentage } from "../utils";

import type {
  GroupedMomentAnalyticsData,
  NarrowedGroupedAnalyticsData,
} from "../../../../modules/stats/server/analytics-service";
import type { GridColDef } from "@mui/x-data-grid-pro";
import type { FC } from "react";

export const AnalyticsSendsTableExpandedIssues: FC<{
  stats: GroupedMomentAnalyticsData["stats"];
}> = ({ stats }) => {
  const { data, overall } = useMemo(() => {
    const deliveries = filterStatType("delivery", stats);
    const errors = filterStatType("error", stats);

    return {
      data: errors.map((item) => item.data),
      overall: { total: deliveries.length + errors.length, count: errors.length },
    };
  }, [stats]);

  const makeErrorMessage = useErrorMessage();

  const rows = useMemo(
    () =>
      data.map((item, id) => ({
        id,
        first_name: item.recipient_data?.first_name ?? "-",
        last_name: item.recipient_data?.last_name ?? "-",
        work_email: item.recipient_data?.email ?? "-",
        delivery_address: item.recipient_data?.display ?? "-",
        error: item.error,
      })),
    [data],
  );

  const columns = useMemo(() => {
    const columnData: GridColDef<(typeof rows)[0]>[] = [
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
      },
      {
        field: "work_email",
        headerName: "Work Email",
        flex: 1,
      },
      {
        field: "delivery_address",
        headerName: "Delivery Address",
        flex: 1,
      },
      {
        field: "error",
        headerName: "Issue",
        flex: 3,
        valueFormatter: (error: NarrowedGroupedAnalyticsData<"error">["error"]) =>
          makeErrorMessage(error),
      },
    ];
    return columnData;
  }, [makeErrorMessage]);

  const percent = calculatePercentage(overall.count, overall.total);

  if (rows.length === 0)
    return <EmptyState symbol="🤞" title="No issues" subtitle="Let’s hope it stays that way..." />;

  return (
    <Stack spacing={2} direction="column">
      <Stack direction="row" spacing={2} alignItems="flex-end">
        <Typography variant="h4">{`${percent}%`}</Typography>
        <Typography variant="body1" color="text.secondary">
          ({overall.count} {overall.count === 1 ? "Recipient" : "Recipients"})
        </Typography>
      </Stack>
      <Typography variant="body2">
        Delivery to these recipients failed. Wherever possible, we’ll provide an explanation of what
        caused the failure.
      </Typography>
      <DataGridPro
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{ toolbar: ExportToolbar }}
        showToolbar
        getRowHeight={() => "auto"}
        getEstimatedRowHeight={() => 50}
        sx={{
          "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": { py: 0.5 },
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": { py: 1 },
          "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": { py: 2 },
          boxShadow: 2,
          backgroundColor: "background.paper",
          "& .MuiDataGrid-cell:hover": {
            color: "primary.main",
          },
        }}
      />
    </Stack>
  );
};

function useErrorMessage(): (error?: NarrowedGroupedAnalyticsData<"error">["error"]) => string {
  return useCallback((error?: NarrowedGroupedAnalyticsData<"error">["error"]) => {
    if (!error) return "Unknown Error";

    if (error.type === "error") {
      return error.data.message;
    }

    return `Bounced. Reason: ${error.data.reason}. Bounce Type: ${error.data.type ?? "-"}. Sub Type ${error.data.sub_type ?? "-"}`;
  }, []);
}
